
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { IFaq } from '@/types';
import { getApiBasepath } from 'api';
import { BannerHalfBlurred } from 'components/common/BannerHalfBlurred';
import { Collapsable } from 'components/common/Collapsable';
import { FeatureCard } from 'components/common/FeatureCard';
import { HowItWorksStep } from 'components/common/HowItWorksStep';
import { Icon } from 'components/common/Icon';
import { Info } from 'components/common/Info';
import { InfoDot } from 'components/common/InfoDot';
import { Markdown } from 'components/common/Markdown';
import { Meta } from 'components/common/Meta';
import { ScrollLink } from 'components/common/ScrollLink';
import { Separator } from 'components/common/Separator';
import { BannerContent } from 'components/how-it-works/BannerContent';
import { emails } from 'constants/';
import { Regions } from 'constants/regions';
import routes from 'constants/routes';
import { useUserRegion } from 'hooks/useUserRegion';
import { BaseLayout } from 'layouts/BaseLayout';
import { GetStaticProps } from 'next';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import Image from 'next/image';
import Link from 'next/link';
import { ReactElement, useEffect, useState } from 'react';
const Shipping = () => {
    const { t } = useTranslation('how_it_works');
    const [packingMethod, setPackingMethod] = useState<'tube' | 'box' | 'flat-pack'>('tube');
    const [faqs, setFaqs] = useState<IFaq[]>();
    const { userRegion } = useUserRegion();
    const isUS = userRegion === Regions.US;
    const getFaqs = async () => {
        const response = await fetch(`${getApiBasepath()}/api/faqs`);
        const data = await response.json();
        const howItWorksFaqs = data.data?.filter((faq: IFaq) => faq.attributes.page === 'SHIPPING');
        setFaqs(howItWorksFaqs);
    };
    function buildTranslationKey(key: string) {
        if (isUS)
            return `${key}_us`;
        return key;
    }
    useEffect(() => {
        getFaqs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const translationComponents = {
        dhlLink: (<a href={isUS ? routes.dhlDropOffPointUS() : routes.dhlDropOffPoint()} className='text-secondary-default underline' target='_blank' rel='noreferrer'/>),
        collectionLink: (<Link href='/how-it-works/shipping#schedulePickup' className='text-secondary-default underline'/>),
        upsLink: (<a href={routes.ups} className='text-secondary-default underline' target='_blank' rel='noreferrer'/>)
    } as const;
    return (<>
      <Meta pageTitle={t('shipping_page_title')} pageDescription={t('shipping_page_desc')} imageName='social-hiw-shipping' relativeUrl={routes.howItWorks.shipping}/>

      <main className='bg-warm-50'>
        <BannerHalfBlurred src='/images/banners/hiw-shipping.png' priority reversed loading='eager'>
          <BannerContent title={t('shipping_banner_title')} description={<>
                {t('shipping_banner_description_1')} <br />
                {t('shipping_banner_description_2')}
              </>}/>
        </BannerHalfBlurred>

        <section className='bg-warm-50 pb-8 pt-12 sm:pb-12 md:pt-16'>
          <div className='container flex justify-center'>
            <div className='w-full max-w-[1120px]'>
              <div className='-mx-2 -my-2 grid grid-cols-1 md:grid-cols-2'>
                <FeatureCard className='col-span-1 mx-2 my-2' iconName='filled-prize' title={t('feature_cards_shipping.1_title')} shortDescription={t(buildTranslationKey('feature_cards_shipping.1_description'))} hasCollapsable={false}/>

                <FeatureCard className='col-span-1 mx-2 my-2' iconName='filled-crossroads' title={t('feature_cards_shipping.2_title')} shortDescription={<p>
                      <Trans i18nKey={buildTranslationKey('how_it_works:feature_cards_shipping.2_description')} components={translationComponents}/>
                    </p>} hasCollapsable={false}/>

                <FeatureCard className='col-span-1 mx-2 my-2' iconName='filled-shield' title={t(buildTranslationKey('feature_cards_shipping.3_title'))} shortDescription={t(buildTranslationKey('feature_cards_shipping.3_description'))} hasCollapsable={false}/>

                <FeatureCard className='col-span-1 mx-2 my-2' iconName='filled-order-tracking' title={t('feature_cards_shipping.4_title')} shortDescription={t('feature_cards_shipping.4_description')} hasCollapsable={false}/>
              </div>
            </div>
          </div>
        </section>

        <section className='hidden bg-emerald-800 sm:block'>
          <div className='container flex flex-col items-center justify-center space-y-3 py-4'>
            <div className='h-6 w-6 md:h-8 md:w-8'>
              <Icon name='organic-product' custom size={24} className='md:hidden'/>
              <Icon name='organic-product' custom size={32} className='hidden md:block'/>
            </div>

            <p className='text-center text-sm font-medium text-cool-50 md:text-base'>
              <span className='font-bold'>{t('fairart_is_the_first')}</span>
              <br />
              <br /> {t('thats_right')} <br /> {t('this_is_just_the_first')}{' '}
              <br />
              {t('watch_this_space')}
            </p>
          </div>
        </section>

        <section className='flex justify-center bg-warm-50 pb-16 pt-12'>
          <div className='container flex flex-col items-center'>
            <h4 className='mb-14 hidden font-medium sm:block'>
              {t('ship_in_3_steps')}
            </h4>
            <h5 className='mb-14 font-medium sm:hidden'>
              {t('ship_in_3_steps')}
            </h5>
            <div className='mb-6 flex flex-col items-center'>
              <InfoDot className='mb-3 min-h-[32px] w-8 bg-primary-default sm:min-h-[48px] sm:w-12'>
                <h5 className='hidden font-medium text-cool-150 sm:block'>1</h5>
                <h6 className='font-medium text-cool-150 sm:hidden'>1</h6>
              </InfoDot>
              <h5 className='hidden font-medium text-cool-900 sm:block'>
                {t('pack_with_care')}
              </h5>
              <h6 className='font-medium text-cool-900 sm:hidden'>
                {t('pack_with_care')}
              </h6>
            </div>
            <HowItWorksStep src='/files/hiw-shipping-step1.svg' alt='dhl-woman-with-cat' description={<p className='text-sm text-cool-600 lg:text-base'>
                  {t('how_it_works_shipping.1_description_1')} <br />
                  <br />
                  {t('how_it_works_shipping.1_description_2')}{' '}
                  <ScrollLink to='packingGuidance' behavior='smooth' className='cursor-pointer text-secondary-default'>
                    {t('how_it_works_shipping.1_description_3')}
                  </ScrollLink>{' '}
                  {t('how_it_works_shipping.1_description_4')} <br />
                  <br /> {t('how_it_works_shipping.1_description_5')}
                </p>} className='mb-16 max-w-[1120px]'/>

            <div className='mb-8 flex flex-col items-center'>
              <InfoDot className='mb-3 min-h-[32px] w-8 bg-primary-default sm:min-h-[48px] sm:w-12'>
                <h5 className='hidden font-medium text-cool-150 sm:block'>2</h5>
                <h6 className='text-base font-medium text-cool-150 sm:hidden'>
                  2
                </h6>
              </InfoDot>
              <h5 className='hidden font-medium text-cool-900 sm:block'>
                {t('ship_within_3_days')}
              </h5>
              <h6 className='font-medium text-cool-900 sm:hidden'>
                {t('ship_within_3_days')}
              </h6>
            </div>

            <HowItWorksStep src='/files/hiw-shipping-step2.svg' alt='' description={<div className='text-sm text-cool-600 lg:text-base'>
                  <p>
                    {t(buildTranslationKey('how_it_works_sellers.3_description'))}
                  </p>

                  <ol className='list-inside list-disc pl-3'>
                    <li>
                      <Trans i18nKey={buildTranslationKey('how_it_works:how_it_works_sellers.3_steps.1')} components={translationComponents}/>
                    </li>
                    <li>
                      <Trans i18nKey={buildTranslationKey('how_it_works:how_it_works_sellers.3_steps.2')} components={translationComponents}/>
                    </li>
                  </ol>

                  {isUS && (<p className='mt-4'>
                      <Trans i18nKey='how_it_works:how_it_works_sellers.3_additional_description_us' components={translationComponents}/>
                    </p>)}
                </div>} className='mb-16 max-w-[1120px]' reversed/>

            <div className='mb-8 flex flex-col items-center'>
              <InfoDot className='mb-3 min-h-[32px] w-8 bg-primary-default sm:min-h-[48px] sm:w-12'>
                <h5 className='hidden font-medium text-cool-150 sm:block'>3</h5>
                <h6 className='text-base font-medium text-cool-150 sm:hidden'>
                  3
                </h6>
              </InfoDot>
              <h5 className='hidden font-medium text-cool-900 sm:block'>
                {t('get_paid_2')}
              </h5>
              <h6 className='font-medium text-cool-900 sm:hidden'>
                {t('get_paid_2')}
              </h6>
            </div>

            <HowItWorksStep src='/files/hiw-shipping-step3.svg' alt='' description={<p className='text-sm text-cool-600 lg:text-base'>
                  {t(buildTranslationKey('how_it_works_shipping.3_description_1'))}
                  <br /> <br />{' '}
                  {t(buildTranslationKey('how_it_works_shipping.3_description_2'))}
                </p>} className='max-w-[1120px]'/>
          </div>
        </section>

        <div className='container flex justify-center'>
          <Separator className='max-w-[1120px] bg-cool-400'/>
        </div>

        <section id='packingGuidance' className='flex flex-col justify-center bg-warm-50 py-12'>
          <div className='container flex w-full max-w-[1120px] flex-col items-center'>
            <h5 className='text-center font-medium md:hidden'>
              {t('packing_guidance')}
            </h5>
            <h4 className='hidden text-center font-medium md:block'>
              {t('packing_guidance')}
            </h4>
            <p className='mb-12 mt-3 text-center text-sm text-cool-600 sm:mb-8 md:mb-8 md:mt-5 md:text-base'>
              {t('were_here_to_help')}
            </p>
          </div>

          <div className='mx-auto flex w-full max-w-[1120px] flex-col items-center justify-center space-y-3 rounded-lg bg-primary-light py-8'>
            <p className='text-center text-lg'>
              <b>
                <Trans i18nKey='how_it_works:check_out_our_essential_packing_guide' components={{
            link: (<a className='text-secondary-default underline' target='_blank' rel='noopener noreferrer' href='/files/fairart-shipping-guidance.pdf'/>)
        }}/>
              </b>
            </p>

            <div className='relative grid grid-cols-2 grid-rows-2 gap-x-8 gap-y-6 rounded-lg bg-primary-default px-7 py-4'>
              <div className='absolute left-1/2 top-1/2 h-[calc(100%-1.5rem)] w-[1px] -translate-x-1/2 -translate-y-1/2 bg-cool-50/20'></div>
              <div className='absolute left-1/2 top-1/2 h-[1px] w-[calc(100%-1.875rem)] -translate-x-1/2 -translate-y-1/2 bg-cool-50/20'></div>

              <div className='flex min-w-[88px] flex-col items-center justify-center space-y-2.5 text-cool-50'>
                <p>{t('framed.title')}</p>
                <Image src='/files/hiw-shipping-tube.png' alt='tube' className='min-w-[75px] object-contain' sizes='20vw' width={75} height={75}/>
              </div>

              <div className='flex min-w-[88px] flex-col items-center justify-center space-y-2.5 text-cool-50'>
                <p>{t('tubes.title')}</p>
                <Image src='/files/hiw-shipping-box.svg' className='min-w-[75px] object-contain' alt='box' sizes='20vw' width={75} height={75}/>
              </div>

              <div className='flex min-w-[88px] flex-col items-center justify-center space-y-2.5 text-cool-50'>
                <p>{t('boxes.title')}</p>
                <Image src='/files/hiw-shipping-box.svg' className='min-w-[75px] object-contain' alt='box' sizes='20vw' width={75} height={75}/>
              </div>

              <div className='flex min-w-[88px] flex-col items-center justify-center space-y-2.5 text-cool-50'>
                <p>{t('flatpacks.title')}</p>
                <Image src='/files/hiw-shipping-flatpack.svg' className='min-w-[75px] object-contain' alt='flat-pack' sizes='20vw' width={75} height={75}/>
              </div>
            </div>
          </div>
        </section>

        <div className='container flex justify-center'>
          <Separator className='max-w-[1120px] bg-cool-400'/>
        </div>

        <section className='flex justify-center bg-warm-50 py-12' id='schedulePickup'>
          <div className='container flex w-full flex-col items-center'>
            <h5 className='text-center font-medium md:hidden'>
              {t('schedule_a_pickup')}
            </h5>
            <h4 className='hidden text-center font-medium md:block'>
              {t('schedule_a_pickup')}
            </h4>
            <p className='mb-8 mt-3 text-center text-sm text-cool-600 md:mt-5 md:text-base'>
              {t('scheduling_dhl_express')}
            </p>

            <div className='w-full max-w-[1120px] rounded-lg border border-solid border-cool-400 bg-cool-50 px-5 py-8 sm:px-8 lg:px-12'>
              {isUS && (<Info className='mb-4'>
                  <p className='text-cool-700'>
                    <b>{t('schedule_pickup_info_us.title')}</b>
                  </p>
                  <p className='text-cool-700'>
                    <Trans i18nKey='how_it_works:schedule_pickup_info_us.description' components={{
                a: (<a href={`mailto:${emails.support}`} className='text-secondary-default underline'/>)
            }}/>
                  </p>
                </Info>)}

              <ul className='list-inside list-decimal text-sm text-cool-600 sm:text-base'>
                <li>
                  {t('go_to')}{' '}
                  <a href='https://mydhl.express.dhl/gb/en/schedule-pickup.html#/schedule-pickup#label-reference' target='_blank' rel='noreferrer' className='hidden text-secondary-default md:inline'>
                    {t('dhl_portal')}
                  </a>
                  <a href='https://mydhl.express.dhl/gb/en/mobile.html#/pickup' target='_blank' rel='noreferrer' className='text-secondary-default md:hidden'>
                    {t('dhl_portal')}
                  </a>
                </li>{' '}
                <br />
                <li>
                  {t('answer_the_questions')}
                  <ul className='ml-6 list-inside list-[lower-alpha]'>
                    <li>
                      {t('do_you_need')}{' '}
                      <span className='font-bold'>{t('no')}</span>
                    </li>
                    <li>
                      {t('select')}{' '}
                      <span className='font-bold'>{t('i_have')}</span>{' '}
                      {t('in_the_dropdown')}
                    </li>
                    <li>
                      {t('enter_your_waybill')}{' '}
                      <ul className='ml-7 list-inside list-[lower-roman]'>
                        <li>{t('you_can_find')}</li>
                      </ul>
                    </li>
                    <li>
                      {t('fill_in_your_country')}{' '}
                      <span className='font-bold'>{t('next')}</span>
                    </li>
                  </ul>{' '}
                  <div className='relative mt-3 min-h-[174px] w-full md:min-h-[280px] md:max-w-[500px]'>
                    <Image src='/files/schedule-a-pickup.png' alt='schedule-a-pickup' width={500} height={280} quality={100} priority/>
                  </div>
                </li>
                <br />
                <li>
                  {t('enter_the_pickup')}{' '}
                  <span className='font-bold'>{t('next')}</span>
                </li>
                <br />
                <li>
                  {t('enter_number_of_packages')}{' '}
                  <span className='font-bold'>(1)</span>
                  {t('total_weight')}{' '}
                  <span className='font-bold'>{t('next')}</span>{' '}
                  <ul className='ml-[60px] list-inside list-[lower-roman]'>
                    <li>{t('if_you_are_unable')}</li>
                  </ul>
                  <div className='relative mt-3 min-h-[95px] w-full md:min-h-[151px] md:max-w-[500px]'>
                    <Image src='/files/what-are-we-picking-up.png' alt='schedule-a-pickup' quality={100} width={500} height={151} priority/>
                  </div>
                </li>
                <br />
                <li>
                  {t('schedule_the_pickup')}{' '}
                  <span className='font-bold'>{t('schedule_pickup')}</span>
                  <div className='relative mt-3 min-h-[125px] w-full md:min-h-[212px] md:max-w-[500px]'>
                    <Image src='/files/when-should-we-pickup-your-shipment.png' alt='schedule-a-pickup' quality={100} width={500} height={212} priority/>
                  </div>
                </li>
              </ul>
              <br />
              <p className='text-sm text-cool-600 sm:text-base'>
                {t('thats_it')}
              </p>
            </div>
          </div>
        </section>

        {faqs && faqs.length > 0 && (<section className='bg-warm-50 pb-16 pt-12 sm:pb-20'>
            <h5 className='text-center font-medium md:hidden'>{t('faqs')}</h5>
            <h4 className='hidden text-center font-medium md:block'>
              {t('faqs')}
            </h4>
            <div className='container mt-8 flex justify-center'>
              <div className='flex w-full max-w-[1120px] flex-col items-center justify-center space-y-4'>
                {faqs.map((faq, index) => (<Collapsable key={index} type='faq' className='w-full rounded-xl border border-solid border-cool-300 bg-cool-50 p-5 sm:p-8' title={faq.attributes.title ? (<Markdown className='font-medium sm:text-xl'>
                          {faq.attributes.title}
                        </Markdown>) : (<p>
                          Sorry, we don&apos;t have a question for this answer
                          yet. Try again later!
                        </p>)}>
                    <div className='mt-3 text-sm text-cool-600 sm:mt-4 sm:text-base'>
                      {faq.attributes.description ? (<Markdown className='custom-answer'>
                          {faq.attributes.description}
                        </Markdown>) : (<p>
                          Sorry, we don&apos;t have an answer yet. Try again
                          later!
                        </p>)}
                    </div>
                  </Collapsable>))}
              </div>
            </div>
          </section>)}
      </main>
    </>);
};
Shipping.getLayout = (page: ReactElement) => (<BaseLayout style='off-white-bg'>{page}</BaseLayout>);
const getStaticProps: GetStaticProps = async () => ({ props: {} });
export default Shipping;

    async function __Next_Translate__getStaticProps__19cd74a83e3__(ctx) {
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/how-it-works/shipping',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__19cd74a83e3__ as getStaticProps }
  